import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import ScreenshotFrame from "../components/ScreenshotFrame";
import BrowserFrame from "../components/BrowserFrame";
import { DEMOS } from "../data/demos";
import { usePageMeta } from "../hooks/usePageMeta";
import { scrollToHash } from "../lib/scroll";

function DemoCard({ demo, flip }) {
  return (
    <article
      className={`card-cr grid items-center gap-10 p-6 sm:p-10 lg:grid-cols-[1.15fr_0.85fr] ${
        flip ? "lg:[&>*:first-child]:order-2" : ""
      }`}
      data-testid={`demo-card-${demo.id}`}
    >
      <div className="relative">
        <BrowserFrame url="greenscape-outdoor-design.com">
          <ScreenshotFrame
            src={`/images/${demo.desktopShot}`}
            label={`${demo.title} — desktop preview`}
            ratio="16 / 10"
            alt={demo.desktopAlt}
          />
        </BrowserFrame>
        <div className="absolute -bottom-8 -right-3 hidden w-[120px] overflow-hidden rounded-[22px] border-4 border-[#262B30] bg-subtle shadow-2xl shadow-black/60 sm:block lg:-right-6">
          <ScreenshotFrame
            src={`/images/${demo.mobileShot}`}
            label={`${demo.title} — phone preview`}
            ratio="9 / 17"
            alt={demo.mobileAlt}
            className="rounded-none border-0"
          />
        </div>
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-serif text-[clamp(24px,2.4vw,32px)] leading-tight text-cream">
            {demo.title}
          </h2>
          <span className="rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.1em] text-accent">
            {demo.tag}
          </span>
        </div>
        <p className="measure mt-4 text-[16.5px] leading-relaxed text-dim">{demo.description}</p>
        <ul className="mt-6 flex flex-wrap gap-2.5" aria-label="Included features">
          {demo.features.map((f) => (
            <li
              key={f}
              className="rounded-full border border-line px-3.5 py-1.5 text-[13px] text-cream/85"
            >
              {f}
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <a
            href={demo.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            data-testid={`demo-live-link-${demo.id}`}
          >
            View live demo
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <Link to="/#contact" className="btn-secondary" data-testid={`demo-request-${demo.id}`}>
            Request a site like this
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Demos() {
  usePageMeta({
    title: "Sample Sites | Clearriver Studios",
    description:
      "Concept sites built for a fictional landscaping company, showing the style, structure, and features a finished Clearriver Studios website includes.",
  });
  return (
    <div className="pt-[72px]">
      <section className="container-cr pb-6 pt-14 sm:pt-20">
        <Reveal>
          <p className="eyebrow flex items-center gap-2.5">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
            Sample work
          </p>
          <h1 className="h-display mt-6 text-[clamp(40px,6vw,72px)]" data-testid="demos-h1">
            Sample sites.
          </h1>
          <p className="measure mt-6 text-[17px] leading-relaxed text-dim">
            These concept sites were built for GreenScape Outdoor Design, a fictional
            landscaping company, to show how a finished Clearriver Studios website looks
            and works. Explore them live on desktop or phone.
          </p>
          <p
            className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-accent/35 bg-accent/[0.08] px-5 py-2.5 text-sm font-medium text-cream"
            data-testid="concept-notice"
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
            Concept work: fictional business, not a real client.
          </p>
        </Reveal>
      </section>

      <section className="container-cr flex flex-col gap-20 py-14 lg:gap-28 lg:py-20">
        {DEMOS.map((demo, i) => (
          <Reveal key={demo.id}>
            <DemoCard demo={demo} flip={i % 2 === 1} />
          </Reveal>
        ))}
      </section>

      <section className="hero-glow border-t border-line py-20 lg:py-28">
        <div className="container-cr text-center">
          <Reveal>
            <h2 className="h-display mx-auto max-w-2xl text-[clamp(28px,4vw,48px)]">
              Want to see what your company&rsquo;s site could look like?
            </h2>
            <Link to="/#contact" className="btn-primary mt-9" data-testid="demos-bottom-cta">
              Request a quote
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
